/**
 * Create/edit dialog for one CMDBuild card, rendered entirely from the schema
 * metadata — there is no per-class form code anywhere in the SPA.
 *
 * Rules encoded here:
 *
 * 1. `system: true` attributes are rendered DISABLED with a note. They are owned
 *    by a pipeline (KEV scan, SBOM export, notification fan-out), so a manual
 *    value would live only until that job next runs. `toRequestBody` drops them.
 * 2. Reference fields are `<select>`s over the card ids the server offered. A
 *    dangling id (the target was deleted meanwhile) is kept as an extra option so
 *    saving an unrelated field cannot silently blank the reference.
 * 3. The server stays the validator. The only client-side check is "required and
 *    empty", so the user is not sent to the server for the obvious case; every
 *    other message (duplicate, unknown lookup, CMDBuild refusal) is the German
 *    server detail shown verbatim.
 */

import { useCallback, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { errorMessage } from '../../lib/api';
import type {
  CmdbAttribute,
  CmdbCardRow,
  CmdbClassMeta,
  CmdbLookupValue,
  CmdbOption,
} from '../../lib/types';

import { initialForm, toRequestBody } from './cardHelpers';
import { classHint } from './classHints';
import { cpePreview, hasCpeFields } from './cpePreview';

export interface CardFormModalProps {
  meta: CmdbClassMeta;
  /** `null` = create. */
  row: CmdbCardRow | null;
  /** Context defaults for creation, e.g. the component a version belongs to. */
  initialValues?: Record<string, unknown>;
  /** Keep everyday fields visible and disclose the remaining fields on demand. */
  primaryFields?: readonly string[];
  /** Context fields remain visible and are submitted, but cannot be changed. */
  readOnlyFields?: readonly string[];
  /** Context note above the fields, e.g. where the defaults came from. */
  notice?: ReactNode;
  /** Reference options per target class, from `GET /api/cmdb/options`. */
  options: Record<string, CmdbOption[]>;
  /** Lookup values per lookup type, from `GET /api/cmdb/meta`. */
  lookups: Record<string, CmdbLookupValue[]>;
  /** Resolves with the saved row; rejects with an `ApiError` to display. */
  onSubmit: (values: Record<string, unknown>) => Promise<unknown>;
  onClose: () => void;
}

export function CardFormModal({
  meta,
  row,
  initialValues,
  primaryFields,
  readOnlyFields,
  notice,
  options,
  lookups,
  onSubmit,
  onClose,
}: CardFormModalProps): ReactNode {
  const [form, setForm] = useState<Record<string, string | boolean>>(() => initialForm(meta,
    row ?? (initialValues ? { id: 0, label: '', code: null, values: initialValues, display: {} } : null)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<readonly string[]>([]);

  const editable = useMemo(() => meta.attributes.filter((a) => !a.system), [meta.attributes]);
  const systemAttrs = useMemo(() => meta.attributes.filter((a) => a.system), [meta.attributes]);
  const mainAttrs = editable.filter((attr) => !primaryFields || primaryFields.includes(attr.name));
  const advancedAttrs = editable.filter((attr) => primaryFields && !primaryFields.includes(attr.name));
  const showCpe = useMemo(() => hasCpeFields(meta.attributes.map((a) => a.name)), [meta.attributes]);
  const cpe = showCpe ? cpePreview(form) : null;

  const set = useCallback((name: string, value: string | boolean): void => {
    setForm((current) => ({ ...current, [name]: value }));
  }, []);

  const submit = useCallback(
    async (event: FormEvent): Promise<void> => {
      event.preventDefault();
      const empty = editable
        .filter((attr) => attr.required && String(form[attr.name] ?? '').trim() === '')
        .map((attr) => attr.name);
      setMissing(empty);
      if (empty.length > 0) {
        setError('Bitte die markierten Pflichtfelder ausfüllen.');
        return;
      }
      setSaving(true);
      setError(null);
      try {
        await onSubmit(toRequestBody(meta, form));
      } catch (caught) {
        setError(errorMessage(caught));
      } finally {
        setSaving(false);
      }
    },
    [editable, form, meta, onSubmit],
  );

  const renderField = (attr: CmdbAttribute, disabled: boolean): ReactNode => {
    const value = form[attr.name];
    const id = `cmdb-${meta.name}-${attr.name}`;
    const invalid = missing.includes(attr.name);

    if (attr.type === 'boolean') {
      return (
        <label className="check" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            onChange={(event) => set(attr.name, event.target.checked)}
          />
          <span>{attr.help}</span>
        </label>
      );
    }

    if (attr.type === 'reference') {
      const target = attr.target_class ?? '';
      const list = options[target] ?? [];
      const current = String(value ?? '');
      // Keep a value that is no longer offered visible instead of dropping it.
      const dangling = current !== '' && !list.some((o) => String(o.id) === current);
      return (
        <select
          id={id}
          value={current}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          onChange={(event) => set(attr.name, event.target.value)}
        >
          <option value="">{attr.required ? '— bitte wählen —' : '— leer —'}</option>
          {dangling ? <option value={current}>#{current} (nicht mehr vorhanden)</option> : null}
          {list.map((option) => (
            <option key={option.id} value={String(option.id)}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (attr.type === 'lookup') {
      const list = lookups[attr.lookup_type ?? ''] ?? [];
      return (
        <select
          id={id}
          value={String(value ?? '')}
          disabled={disabled || list.length === 0}
          aria-invalid={invalid || undefined}
          onChange={(event) => set(attr.name, event.target.value)}
        >
          <option value="">— leer —</option>
          {list.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label || option.code}
            </option>
          ))}
        </select>
      );
    }

    if (attr.type === 'text') {
      return (
        <textarea
          id={id}
          rows={4}
          value={String(value ?? '')}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          onChange={(event) => set(attr.name, event.target.value)}
        />
      );
    }

    const inputType =
      attr.type === 'date'
        ? 'date'
        : attr.type === 'dateTime'
          ? 'datetime-local'
          : attr.type === 'double' || attr.type === 'integer'
            ? 'number'
            : 'text';
    return (
      <input
        id={id}
        type={inputType}
        step={attr.type === 'double' ? 'any' : undefined}
        value={String(value ?? '')}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={(event) => set(attr.name, event.target.value)}
      />
    );
  };

  const field = (attr: CmdbAttribute, disabled: boolean): ReactNode => (
    <div className="field" key={attr.name}>
      <label htmlFor={`cmdb-${meta.name}-${attr.name}`}>
        {attr.label}
        {attr.required ? ' *' : ''}
      </label>
      {renderField(attr, disabled || !!readOnlyFields?.includes(attr.name))}
      {attr.type === 'boolean' ? null : <div className="hint">{attr.help}</div>}
      {missing.includes(attr.name) ? <div className="err">Pflichtfeld</div> : null}
    </div>
  );

  return (
    <Modal
      title={row ? `${meta.label} bearbeiten` : `${meta.label} anlegen`}
      subtitle={row ? row.label : classHint(meta.name) ?? undefined}
      size="wide"
      dismissable={!saving}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="primary" form="cmdb-card-form" disabled={saving}>
            {saving ? <Spinner label="Speichert…" /> : row ? 'Speichern' : 'Anlegen'}
          </button>
        </>
      }
    >
      <form id="cmdb-card-form" onSubmit={submit} className="stamm-form">
        {error ? <div className="banner error">{error}</div> : null}
        {notice ? <div className="banner" role="status">{notice}</div> : null}
        <div className="stamm-grid">{mainAttrs.map((attr) => field(attr, saving))}</div>
        {showCpe ? (
          <div className="field stamm-cpe">
            <label htmlFor={`cmdb-${meta.name}-cpe-preview`}>CPE (Vorschau)</label>
            <output id={`cmdb-${meta.name}-cpe-preview`} className="mono" aria-live="polite">
              {cpe ?? 'Kein CPE: Vendor oder Hersteller und Product oder Bezeichnung fehlen.'}
            </output>
            <div className="hint">
              So setzt der SBOM-Export den CPE zusammen. Leere Felder werden als * exportiert; ohne Vendor bzw.
              Product gelten Hersteller bzw. Bezeichnung.
            </div>
          </div>
        ) : null}
        {advancedAttrs.length > 0 ? (
          <details className="stamm-system" open={advancedAttrs.some((attr) => missing.includes(attr.name)) || undefined}>
            <summary>Weitere Angaben & technische Kennungen</summary>
            <div className="stamm-grid">{advancedAttrs.map((attr) => field(attr, saving))}</div>
          </details>
        ) : null}

        {systemAttrs.length > 0 ? (
          <details className="stamm-system">
            <summary>
              {systemAttrs.length} automatisch gepflegte{systemAttrs.length === 1 ? 's' : ''} Feld
              {systemAttrs.length === 1 ? '' : 'er'}
            </summary>
            <p className="muted">Diese Werte setzt AssetRadar automatisch; Änderungen hier werden nicht gespeichert.</p>
            <div className="stamm-grid">{systemAttrs.map((attr) => field(attr, true))}</div>
          </details>
        ) : null}
      </form>
    </Modal>
  );
}

export default CardFormModal;
